import React, { createContext, useContext, useState, useCallback } from 'react';
import { Snackbar as MuiSnackbar, Alert, AlertTitle } from '@mui/material';
import { colors, fonts, spacing, shadows } from '../../constants';
import { borderRadius } from '../../constants/fonts';

const SnackbarContext = createContext(null);

export const SnackbarProvider = ({ children }) => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    type: 'success', // 'success', 'error', 'warning', 'info'
    title: null,
    duration: 4000,
  });

  const showSnackbar = useCallback((message, type = 'success', options = {}) => {
    setSnackbar({
      open: true,
      message,
      type,
      title: options.title || null,
      duration: options.duration || 4000,
    });
  }, []);

  const hideSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const getAlertColor = (type) => {
    switch (type) {
      case 'success':
        return colors.success;
      case 'error':
        return colors.error;
      case 'warning':
        return colors.warning;
      case 'info':
        return colors.info;
      default:
        return colors.success;
    }
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar, hideSnackbar }}>
      {children}
      <MuiSnackbar
        open={snackbar.open}
        autoHideDuration={snackbar.duration}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            borderRadius: borderRadius.md,
            boxShadow: shadows.lg,
          },
        }}
      >
        <Alert
          onClose={hideSnackbar}
          severity={snackbar.type}
          variant="filled"
          sx={{
            borderRadius: borderRadius.md,
            backgroundColor: getAlertColor(snackbar.type),
            color: colors.textWhite,
            fontSize: fonts.fontSizeMd,
            fontWeight: fonts.weightMedium,
            boxShadow: shadows.lg,
            '& .MuiAlert-icon': {
              color: colors.textWhite,
            },
            '& .MuiAlert-message': {
              width: '100%',
            },
          }}
        >
          {snackbar.title && (
            <AlertTitle
              sx={{
                fontSize: fonts.fontSizeLg,
                fontWeight: fonts.weightSemiBold,
                color: colors.textWhite,
                marginBottom: spacing.xs,
              }}
            >
              {snackbar.title}
            </AlertTitle>
          )}
          {snackbar.message}
        </Alert>
      </MuiSnackbar>
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};

